import { useState } from 'react';
import Button from 'src/components/buttons/button';
import config from 'config/config';
import Icon from 'src/components/icon';

const PROGRAM_URL = `${config.cdn.navigator}living-with-high-blood-pressure/`;

const getMenu = () => (
  <section className="navigator__contents">
    <div className="navigator__contents-header">Contents</div>
    <ul className="navigator__contents__list">
      <li><div className="navigator__contents__number">1.</div><div className="navigator__contents__content-text"><a href="#1">How to understand a blood pressure reading</a></div></li>
      <li><div className="navigator__contents__number">2.</div><div className="navigator__contents__content-text"><a href="#2">{`Watch Ann's story of how she tackled her blood pressure`}</a></div></li>
      <li><div className="navigator__contents__number">3.</div><div className="navigator__contents__content-text"><a href="#3">Inside your blood vessels</a></div></li>
      <li><div className="navigator__contents__number">4.</div><div className="navigator__contents__content-text"><a href="#4">{`What's your heart age?`}</a></div></li>
    </ul>
  </section>
);

const getPage = () => (
  <div className="navigator">
    <section className="navigator__content">
      <h1 className="navigator__title">
        {'Week 2: Understanding your diagnosis'}
      </h1>
      <p>
        Understanding all the terms and phrases when you get a diagnosis is the first step to feeling in control.
      </p>
      <div className="visible-xs">{getMenu()}</div>
      <h2>How to read your blood pressure</h2>
      <p>{`When you're diagnosed with high blood pressure, you'll come across a range of medical terms and your doctor will probably use specific numbers to describe your condition. We're here to help you make sense of it all.`}</p>
      <p>{`Everyone should know their blood pressure. You can make an appointment at your GP practice to get it checked, or just ask for it to be taken while you're there for an appointment.`}</p>
      <img className="navigator__image-full" src={`${PROGRAM_URL}image_1.jpg`} />
      <p>{`Your blood pressure reading is made up of 2 numbers. You might hear your reading described as one number 'over' another. For example '120 over 80'.`}</p>
      <p>
        <strong>Systolic pressure:</strong> This is the highest level of your blood pressure, when your heart
        contracts to pump blood.
      </p>
      <p>
        <strong>Diastolic pressure:</strong> This is the lowest level of your blood pressure, when your heart relaxes between beats.
      </p>
      <p>mmHg is a unit for measuring blood pressure.</p>
      <a href="https://www.bhf.org.uk/heart-matters-magazine/medical/high-blood-pressure-latest-news" rel="noreferrer" target="_blank">
        <Button
          icon="open-right"
          iconLeftAligned={false}
          text={'Understand your blood pressure reading'}
        />
      </a>
      <h2>{`Ann's story`}</h2>
      <p>
        When Ann found out she had high blood pressure, it was a total shock.
        She shares her story of how some small changes to her lifestyle helped make a big difference.
      </p>
      <div className="navigator__video-container">
        <iframe
          allow="autoplay; encrypted-media; fullscreen"
          src="https://www.youtube.com/embed/UuKnpFrFVR4"
        />
      </div>
      <br />
      <h2>Why does high blood pressure matter?</h2>
      <section className="navigator__section-half">
        <p>{`High blood pressure doesn't often have symptoms, but that doesn't mean it's not serious.`}</p>
        <p>
          Normal arteries (the tubes that carry blood from your heart around your body) are stretchy to cope with your blood pressure going up and down.
          If you have high blood pressure, your arteries lose their stretchiness and become stiff or narrow.
          This makes it easier for fatty material to clog up them up.
        </p>
        <p>
          When your arteries are clogged, there is less space for your blood to flow through
          making it harder for your blood to deliver oxygen to your heart and other organs. This
          can lead to a heart attack or stroke.
        </p>
      </section>
      <img className="navigator__image-half" src={`${PROGRAM_URL}image-2.png`} />
      <a href="https://www.bhf.org.uk/publications/heart-conditions/blood-pressure" rel="noreferrer" target="_blank">
        Order or download our booklet to find out more about managing your diagnosis {'>'}
      </a>
      <h2>{`What's your heart age?`}</h2>
      <section className="navigator__section-half">
        <p>
          {`We've teamed up with NHS Choices to bring you a new way to check your risk of having a heart attack or stroke. Find out how your heart age compares to your real age and find out how you can improve your result.`}
        </p>
        <a href="https://www.bhf.org.uk/heart-health/risk-factors/check-your-heart-age" rel="noreferrer" target="_blank">
          <Button
            icon="open-right"
            iconLeftAligned={false}
            text="Check your heart age"
          />
        </a>
      </section>
      <img className="navigator__image-half" src={`${PROGRAM_URL}image5.jpg`} />

      <img alt="Content provided by BHF" className="navigator__image-full navigator__banner-footer" src={`${PROGRAM_URL}bhf-content.jpg`} />
    </section>
  </div>
);

function Footer() {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;
  return (
    <div className="navigator__footer">
      <img alt="Woman with headset" className="navigator__footer__image" src={`${PROGRAM_URL}cta-helpline.png`} />
      <div className="navigator__footer__inner">
        <div className="navigator__footer__title">
          Call our Heart Helpline
        </div>
        <div className="navigator__footer__description">
          Our Heart Helpline has Cardiac Nurses available to answer your questions. Call us on 0300 330 3311 or email <a className="navigator__footer__link" href="mailto:hearthelpline@bhf.org.uk">hearthelpline@bhf.org.uk</a>
        </div>
        <div className="navigator__footer__close" onClick={() => setVisible(false)}>
          <Icon icon="cross" />
        </div>
      </div>
    </div>
  );
}

const obj = {
  getPage,
  getMenu,
  Footer,
  getSeoTitle: 'Living with high blood pressure | HealthUnlocked',
};

export default obj;
